import * as React from 'react';

import { useLoggedInEmployeeId } from '~/components/Auth';
import { useSyncedLocalStorage } from '~/hooks/useLocalStorage';

const CHATUI_CHANGED_EVENT = 'solairis-chatui-changed';

export const useChatUI = () => {
  const loggedInId = useLoggedInEmployeeId();

  const storageKey = React.useMemo(() => {
    return loggedInId ? `solairis.chatui.${loggedInId}` : null;
  }, [loggedInId]);

  const {
    value: isOpen,
    setValue: setIsOpen,
    refresh,
  } = useSyncedLocalStorage<boolean>(
    storageKey ?? 'solairis.chatui.unauthenticated',
    false,
  );

  const writeOpen = React.useCallback(
    (open: boolean) => {
      if (!storageKey) return;
      setIsOpen(open);
      window.dispatchEvent(new Event(CHATUI_CHANGED_EVENT));
    },
    [setIsOpen, storageKey],
  );

  React.useEffect(() => {
    if (!storageKey) return;

    const onStorage = (e: StorageEvent) => {
      if (e.key === storageKey) {
        refresh();
      }
    };

    const onLocal = () => {
      refresh();
    };

    window.addEventListener('storage', onStorage);
    window.addEventListener(CHATUI_CHANGED_EVENT, onLocal);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener(CHATUI_CHANGED_EVENT, onLocal);
    };
  }, [refresh, storageKey]);

  const open = React.useCallback(() => writeOpen(true), [writeOpen]);
  const close = React.useCallback(() => writeOpen(false), [writeOpen]);
  const toggle = React.useCallback(
    () => writeOpen(!isOpen),
    [isOpen, writeOpen],
  );

  return {
    isOpen: !!storageKey && isOpen,
    open,
    close,
    toggle,
  };
};
